import * as React from "react";
import {
  withStyles,
  WithStyles,
  createStyles,
  StyleRules
} from "@material-ui/core/styles";

// store
import { connect } from "react-redux";
import { AppState } from "@stores/type";
import { UserState } from "@stores/domain/user/type";
import { FacilityState } from "@stores/domain/mgr/GroupHome/facility/types";
import { UsersInFacilityState as GroupHomeUsersInFacilityState } from "@stores/domain/mgr/GroupHome/userInFacility/types";

// constants
import {
  PRINT_PAGE_HEIGHT,
  PRINT_PAGE_PADDING,
  PRINT_PAGE_WIDTH,
  PRINT_PAGE_MARGIN_BOTTOM
} from "@/constants/styles";
import ClassNames from "classnames";
import {
  SprintType,
  DONE_STATE,
  ACHIEVEMENT_STATE,
  PLAN_CHANGE_STATE
} from "@constants/mgr/GroupHome/variables";

// utils
import { getUrlParams } from "@/utils/url";
import { dateToLocalisedString } from "@/utils/date";
import getSnapOrRealName from "@utils/domain/mgr/getSnapOrRealName";
import getSnapOrRealRole from "@utils/domain/mgr/getSnapOrRealRole";
import trimString from "@utils/dataNormalizer/trimString";
import { SupportPlanGroupHomeState } from "@stores/domain/mgr/GroupHome/supportPlan/types";

const styles = (): StyleRules =>
  createStyles({
    page: {
      minHeight: PRINT_PAGE_HEIGHT,
      width: PRINT_PAGE_WIDTH,
      margin: `0 auto ${PRINT_PAGE_MARGIN_BOTTOM}px`,
      padding: `10px ${PRINT_PAGE_PADDING / 2}px`,
      backgroundColor: "#fff",
      boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.5)",
      "&:last-child": {
        margin: "0 auto"
      }
    },
    header: {
      display: "flex",
      justifyContent: "space-between"
    },
    createdDate: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: 12,
      letterSpacing: "0.1em"
    },
    planningPeriodDate: {
      letterSpacing: "0.1em",
      fontSize: 10
    },
    entryField: {
      marginBottom: 28
    },
    entryFieldColumn: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: 8
    },
    entryFieldContainer: {
      fontSize: 10,
      borderBottom: "1px solid #000",
      width: "45%",
      paddingLeft: 8,
      paddingRight: 8
    },
    entryFieldLabel: {
      fontSize: 10,
      marginBottom: 5
    },
    entryFieldContentContainer: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: 3,
      minHeight: "15px"
    },
    entryFieldMark: {
      color: "#9E9E9E",
      fontSize: 12,
      lineHeight: 1.23
    },
    tableContainer: {
      fontSize: 10,
      marginBottom: 21,
      whiteSpace: "pre-line"
    },
    tableTitle: {
      fontWeight: "bold",
      borderBottom: "1px solid #000",
      paddingBottom: 5,
      marginBottom: 8,
      display: "flex",
      justifyContent: "space-between"
    },
    tableSubtitle: {
      fontWeight: 500
    },
    dateContainer: {
      marginRight: 10,
      "&:last-child": {
        marginRight: 0
      }
    },
    table: {
      borderCollapse: "collapse",
      borderSpacing: 0,
      border: "2px solid",
      textAlign: "left",
      tableLayout: "fixed",
      wordWrap: "break-word",
      overflowWrap: "break-word",
      "&.sprintGoal": {
        borderBottom: "none",
        "&:last-child": {
          borderBottom: "2px solid"
        }
      },
      "&.fullWidth": {
        width: "100%"
      },
      "& th": {
        fontWeight: "normal",
        borderRight: "1px solid",
        padding: "3px 8px",
        verticalAlign: "top"
      },
      "& td": {
        padding: "4px 12px",
        fontSize: 10,
        letterSpacing: 0.6,
        height: 24,
        color: "#424242",
        "&.label": {
          textAlign: "center"
        },
        "&.prise": {
          textAlign: "right"
        },
        "&.small": {
          height: 100
        },
        "&.middle": {
          height: 200
        },
        "&.large": {
          height: 300
        },
        "&.sssize": {
          width: 40
        },
        "&.ssize": {
          width: 60
        },
        "&.msize": {
          width: 67.5
        },
        "&.lsize": {
          width: 100
        },
        "&.llsize": {
          width: 180
        },
        "&.borderBold": {
          borderRight: "2px solid"
        },
        "&.borderDashed": {
          borderRight: "1px dashed"
        },
        "&.tableCellPart": {
          padding: "0 12px",
          borderRight: "1px solid"
        }
      },
      "& tr": {
        borderBottom: "1px solid",
        "&.borderBold": {
          borderBottom: "2px solid"
        }
      }
    },
    evaluationTable: {
      marginBottom: 10,
      "&:last-child": {
        marginBottom: 0
      }
    },
    primaryTableHead: {
      width: 98
    },
    secondaryTableHead: {
      width: 98
    },
    sprintTableItem: {
      "&:last-child": {
        borderBottom: "none"
      }
    },
    scheduleTableHead: {
      color: "#424242"
    },
    implementDate: {
      fontWeight: "normal",
      letterSpacing: "0.1em"
    },
    title: {
      margin: 0,
      paddingBottom: 12,
      fontSize: 18,
      fontWeight: "normal",
      letterSpacing: 1.2,
      textAlign: "center",
      color: "rgba(0, 0, 0, 0.84)"
    },
    nameContainer: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: 16
    },
    author: {
      marginBottom: 10
    },
    name: {
      marginLeft: 2,
      marginRight: 20
    },
    userName: {
      width: 368,
      marginBottom: 18
    },
    counts: {
      fontSize: 10,
      letterSpacing: "0.1em"
    },
    signature: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "flex-end",
      paddingBottom: 10
    },
    signatureDate: {
      fontSize: 14,
      display: "flex",
      marginRight: 20,
      "& :nth-of-type(n+2)": {
        marginLeft: 48
      }
    },
    agreeTextContainer: {
      marginTop: 3
    },
    agreeText: {
      fontSize: 14
    },
    userNameContainer: {
      width: 306,
      borderBottomColor: "#999"
    },
    userEntryFieldLabel: {
      fontSize: 12,
      paddingBottom: 5
    },
    userEntryFieldContentContainer: {
      textAlign: "right",
      fontSize: 16,
      height: "24px"
    },
    hideStaffComment: {
      display: "none"
    },
    "@media print": {
      page: {
        width: "172mm",
        height: "251mm",
        minHeight: 0,
        padding: 0,
        margin: "0 auto",
        boxShadow: "none",
        pageBreakAfter: "always",
        "&:last-child": {
          pageBreakAfter: "auto"
        }
      },
      section: {
        pageBreakInside: "avoid"
      }
    }
  });

/**
 * type
 */
type SprintData = {
  id?: number; // 登録済みの場合
  number: number; // 短期目標番号
  sprint_type: SprintType; // 目標種別
  sprint_goal: string; // 目標
  adopt_info: string; // 本人の取り組み内容
  support_info: string; // 職員の支援内容
  sprint_start_date: string; // 設定日
  sprint_end_date: string; // 達成予定日
  sprint_result: number | null; // 振り返り実施
  sprint_review: string; // 振り返りコメント
  sprint_user_evaluation: number | null; // 本人振り返り実施
  sprint_user_review: string; // 本人振り返りコメント
  sprint_staff_evaluation: number | null; // 職員振り返り実施
  sprint_staff_review: string; // 職員振り返りコメント
  user_hope: string | null; // 本人の希望
  necessity_plan_change: number | null; // 計画変更の必要性
};

type DisplayOptions = {
  display_info?: string;
  display_evaluation?: string;
  display_comment?: string;
  display_minutes?: string;
  display_icon?: string;
};

type StateProps = {
  user: UserState;
  userInFacility: GroupHomeUsersInFacilityState["user"];
  facility: FacilityState;
  privateSupportPlan: SupportPlanGroupHomeState["privateSupportPlan"];
};
type OwnProps = {
  uifId: string;
  supportPlanId: string;
  query: string;
};
type DateData = {
  title: string | null;
  date: string | null;
};

type Props = StateProps & OwnProps & WithStyles<typeof styles>;

type SheetOwnProps = {
  userDetail: {
    nameSei?: string;
    nameMei?: string;
    nameSeiKana?: string;
    nameMeiKana?: string;
    dateBirth?: string;
    gender?: string;
    disabilitySupportClass?: string;
    defPickup?: string;
    facilityName: string;
    recipientNumber?: string;
  };
  fixPlanData: SupportPlanGroupHomeState["privateSupportPlan"];
  displayOptions: DisplayOptions;
  facilityState: FacilityState;
};
type SectionTableOwnProps = {
  sectionTitle: string;
  sectionPlanData: {
    itemTitle: string;
    content: string | null;
    isContentEmpty?: boolean;
  }[];
  sprintPlanData?: SprintData[];
  implementDate?: string | null;
  displayOptions?: DisplayOptions;
  secondaryStyle?: boolean;
  subHeading?: JSX.Element;
  schedule?: boolean;
  possiblyHidden?: boolean;
  shortOrLongTermGoalData?: SprintData;
};
type SheetProps = SheetOwnProps & WithStyles<typeof styles>;

type SectionTableProps = SectionTableOwnProps & WithStyles<typeof styles>;

type EvaluationTableProps = {
  sprintPlanDataItem: SprintData;
};

type SectionPlanDataItem = {
  itemTitle: string;
  content: string | null;
  isContentEmpty?: boolean;
};

const EvaluationGroupHomePrintCore = (props: Props): JSX.Element | null => {
  const {
    classes,
    privateSupportPlan,
    user,
    facility,
    userInFacility,
    query
  } = props;

  const queryParameters: DisplayOptions = getUrlParams(query);
  if (
    !privateSupportPlan ||
    Object.keys(privateSupportPlan).length === 0 ||
    Object.keys(userInFacility.user_in_facility).length === 0 ||
    !userInFacility.user_in_facility.id ||
    !user
  ) {
    return null;
  }

  const userDetail = {
    nameSei: userInFacility.user_in_facility.name_sei,
    nameMei: userInFacility.user_in_facility.name_mei,
    nameSeiKana: userInFacility.user_in_facility.name_sei_kana,
    nameMeiKana: userInFacility.user_in_facility.name_mei_kana,
    dateBirth: userInFacility.user_in_facility.date_birth,
    gender: userInFacility.user_in_facility.gender,
    disabilitySupportClass:
      userInFacility.user_in_facility.classify_disability_support,
    defPickup: userInFacility.user_in_facility.def_pickup,
    facilityName: user.facility_name,
    recipientNumber: userInFacility.user_in_facility.recipient_number
  };
  return (
    <Sheet
      classes={classes}
      userDetail={userDetail}
      fixPlanData={privateSupportPlan}
      displayOptions={queryParameters}
      facilityState={facility}
    />
  );
};

const SectionTable = (props: SectionTableProps): JSX.Element => {
  const {
    classes,
    sectionTitle,
    sectionPlanData,
    sprintPlanData,
    implementDate,
    displayOptions,
    secondaryStyle,
    subHeading,
    schedule,
    possiblyHidden,
    shortOrLongTermGoalData
  } = props;

  // 個別目標は全て空だと表示しない
  if (sprintPlanData) {
    const isSprintPlanDataEmpty = !sprintPlanData.some((sprintPlanDataItem) => {
      return (
        trimString(sprintPlanDataItem.adopt_info) ||
        trimString(sprintPlanDataItem.support_info) ||
        trimString(sprintPlanDataItem.sprint_goal) ||
        trimString(sprintPlanDataItem.sprint_start_date) ||
        trimString(sprintPlanDataItem.sprint_end_date)
      );
    });
    if (isSprintPlanDataEmpty) return <></>;
  }

  // possiblyHiddenのときは何か一つデータがあれば表示するが、何もなければ表示せず関数を終了する
  if (possiblyHidden) {
    const isContentAllEmpty = !sectionPlanData.some((item) =>
      trimString(item.content)
    );
    if (isContentAllEmpty) return <></>;
  }

  // プログラム（1日の流れ）では時間とプログラムが全て空欄の場合は表示しない
  if (schedule) {
    const isScheduleAllEmpty = !sectionPlanData.some(
      (item) => !item.isContentEmpty
    );
    if (isScheduleAllEmpty) return <></>;
  }

  const EvaluationTable = (
    evaluationTableProps: EvaluationTableProps
  ): JSX.Element => {
    const { sprintPlanDataItem } = evaluationTableProps;
    const isAbleToShowGeneralEvaluation =
      typeof sprintPlanDataItem.sprint_result === "number" ||
      trimString(sprintPlanDataItem.sprint_review);
    const isAbleToShowSelfEvaluation =
      typeof sprintPlanDataItem.sprint_user_evaluation === "number" ||
      trimString(sprintPlanDataItem.sprint_user_review);
    const isAbleToShowUserHope = trimString(sprintPlanDataItem.user_hope);
    const isAbleToShowStaffEvaluation =
      displayOptions &&
      displayOptions.display_evaluation &&
      (typeof sprintPlanDataItem.sprint_staff_evaluation === "number" ||
        trimString(sprintPlanDataItem.sprint_staff_review));
    const isAbleToShowPlanChange =
      typeof sprintPlanDataItem.necessity_plan_change === "number";
    if (
      !isAbleToShowGeneralEvaluation &&
      !isAbleToShowSelfEvaluation &&
      !isAbleToShowUserHope &&
      !isAbleToShowStaffEvaluation &&
      !isAbleToShowPlanChange
    )
      return <></>;
    return (
      <table className={`${classes.table} fullWidth sprintGoal`}>
        <tbody>
          {isAbleToShowGeneralEvaluation ? (
            <tr>
              <th className={classes.primaryTableHead}>実施</th>
              <td className="tableCellPart msize">
                {sprintPlanDataItem.sprint_result !== null
                  ? DONE_STATE[sprintPlanDataItem.sprint_result]
                  : ""}
              </td>
              <td>
                {sprintPlanDataItem.sprint_review
                  ? sprintPlanDataItem.sprint_review
                  : ""}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {isAbleToShowSelfEvaluation ? (
            <tr>
              <th className={classes.primaryTableHead}>本人の評価</th>
              <td className="tableCellPart msize">
                {sprintPlanDataItem.sprint_user_evaluation !== null
                  ? ACHIEVEMENT_STATE[sprintPlanDataItem.sprint_user_evaluation]
                  : ""}
              </td>
              <td>
                {sprintPlanDataItem.sprint_user_review
                  ? sprintPlanDataItem.sprint_user_review
                  : ""}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {isAbleToShowUserHope ? (
            <tr>
              <th className={classes.primaryTableHead}>本人の希望</th>
              <td colSpan={2}>
                {sprintPlanDataItem.user_hope
                  ? sprintPlanDataItem.user_hope
                  : ""}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {isAbleToShowStaffEvaluation ? (
            <tr>
              <th className={classes.primaryTableHead}>職員からの評価</th>
              <td className="tableCellPart msize">
                {sprintPlanDataItem.sprint_staff_evaluation !== null
                  ? ACHIEVEMENT_STATE[
                      sprintPlanDataItem.sprint_staff_evaluation
                    ]
                  : ""}
              </td>
              <td>
                {sprintPlanDataItem.sprint_staff_review
                  ? sprintPlanDataItem.sprint_staff_review
                  : ""}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {isAbleToShowPlanChange ? (
            <tr>
              <th className={classes.primaryTableHead}>計画変更の必要性</th>
              <td colSpan={2}>
                {sprintPlanDataItem.necessity_plan_change !== null
                  ? PLAN_CHANGE_STATE[sprintPlanDataItem.necessity_plan_change]
                  : ""}
              </td>
            </tr>
          ) : (
            <></>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <section className={classes.section}>
      <div className={classes.tableTitle}>
        <span>{sectionTitle}</span>
        {implementDate && (
          <span className={classes.implementDate}>
            実施日:
            {implementDate === "-"
              ? implementDate
              : dateToLocalisedString(implementDate, "YYYY年M月D日")}
          </span>
        )}
        {subHeading && (
          <span className={classes.tableSubtitle}>{subHeading}</span>
        )}
      </div>
      {sprintPlanData ? (
        sprintPlanData.map(
          (planKey, index): JSX.Element => {
            const keyIndex = index;
            return (
              <React.Fragment key={keyIndex}>
                {(planKey.sprint_goal ||
                  planKey.adopt_info ||
                  planKey.support_info ||
                  planKey.sprint_start_date ||
                  planKey.sprint_end_date ||
                  planKey.sprint_result ||
                  planKey.sprint_review ||
                  planKey.sprint_user_evaluation ||
                  planKey.sprint_user_review ||
                  planKey.sprint_staff_evaluation ||
                  planKey.sprint_staff_review ||
                  planKey.user_hope ||
                  planKey.necessity_plan_change) && (
                  <div className={classes.evaluationTable}>
                    <EvaluationTable sprintPlanDataItem={planKey} />
                  </div>
                )}
              </React.Fragment>
            );
          }
        )
      ) : (
        <>
          <table
            className={`${classes.table} fullWidth ${
              shortOrLongTermGoalData && "sprintGoal"
            }`}
          >
            <tbody>
              {sectionPlanData.map((planKey, index) => {
                const keyNumber = index;
                if (planKey.isContentEmpty)
                  return <React.Fragment key={keyNumber} />;
                return (
                  <tr
                    className={`${
                      displayOptions &&
                      !displayOptions.display_comment &&
                      planKey.itemTitle === "職員コメント"
                        ? classes.hideStaffComment
                        : ""
                    } ${shortOrLongTermGoalData && classes.sprintTableItem}`}
                    key={keyNumber}
                  >
                    {secondaryStyle ? (
                      <th className={classes.secondaryTableHead}>
                        {planKey.itemTitle}
                      </th>
                    ) : (
                      <th
                        className={ClassNames(
                          classes.primaryTableHead,
                          schedule && classes.scheduleTableHead
                        )}
                      >
                        {planKey.itemTitle}
                      </th>
                    )}
                    <td>{planKey.content}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {shortOrLongTermGoalData && (
            <EvaluationTable sprintPlanDataItem={shortOrLongTermGoalData} />
          )}
        </>
      )}
    </section>
  );
};

const Sheet = (props: SheetProps): JSX.Element => {
  const { classes, userDetail, fixPlanData, displayOptions } = props;

  const { author, evaluation_authorizer } = fixPlanData;
  // 作成者名
  const authorValue = getSnapOrRealName(author, "");
  // 評価者名
  const evaluationAuthorizerName = getSnapOrRealName(evaluation_authorizer, "");
  // 評価者役職
  const evaluationAuthorizerRole = getSnapOrRealRole(evaluation_authorizer, "");

  /**
   * support_plan_goalは入力していないnumberは返って来ないので、ない場合は初期値でセットする
   */
  const supplementSupportPlanGoal = (
    supportPlanGoal: SupportPlanGroupHomeState["privateSupportPlan"]["support_plan_goal"]
  ): {
    longTerm: SprintData;
    shortTerm: SprintData[];
    individuals: SprintData[];
  } => {
    const numberOfLongOrShort = 1;
    const convertPrivateToRecord = (
      res:
        | SupportPlanGroupHomeState["privateSupportPlan"]["support_plan_goal"][0]
        | undefined,
      defaultNumber: number,
      defaultSprintType: SprintType
    ): SprintData => {
      return res
        ? {
            id: res.id,
            number: res.number,
            sprint_type: res.sprint_type,
            sprint_goal: res.sprint_goal || "",
            adopt_info: res.adopt_info || "",
            support_info: res.support_info || "",
            sprint_start_date: res.sprint_start_date || "",
            sprint_end_date: res.sprint_end_date || "",
            sprint_result:
              typeof res.sprint_result === "number" ? res.sprint_result : null,
            sprint_review: res.sprint_review || "",
            sprint_user_evaluation:
              typeof res.sprint_user_evaluation === "number"
                ? res.sprint_user_evaluation
                : null,
            sprint_user_review: res.sprint_user_review || "",
            sprint_staff_evaluation:
              typeof res.sprint_staff_evaluation === "number"
                ? res.sprint_staff_evaluation
                : null,
            sprint_staff_review: res.sprint_staff_review || "",
            user_hope: res.user_hope || "",
            necessity_plan_change:
              typeof res.necessity_plan_change === "number"
                ? res.necessity_plan_change
                : null
          }
        : {
            number: defaultNumber,
            sprint_type: defaultSprintType,
            sprint_goal: "",
            adopt_info: "",
            support_info: "",
            sprint_start_date: "",
            sprint_end_date: "",
            sprint_result: null,
            sprint_review: "",
            sprint_user_evaluation: null,
            sprint_user_review: "",
            sprint_staff_evaluation: null,
            sprint_staff_review: "",
            user_hope: "",
            necessity_plan_change: null
          };
    };

    const supportPlanGoalLong = convertPrivateToRecord(
      supportPlanGoal.find((g) => g.sprint_type === SprintType.LONG),
      numberOfLongOrShort,
      SprintType.LONG
    );
    const supportPlanGoalShort = supportPlanGoal
      .filter((g) => g.sprint_type === SprintType.SHORT)
      .map((shortGoal) =>
        convertPrivateToRecord(shortGoal, numberOfLongOrShort, SprintType.SHORT)
      );
    const tmpSupportPlanGoalIndividuals = supportPlanGoal.filter(
      (g) => g.sprint_type === SprintType.INDIVIDUAL
    );
    const individualNumbers = [1, 2, 3, 4, 5]; // 個別目標 1~5
    const supportPlanGoalIndividuals = individualNumbers.map((num) => {
      const res = tmpSupportPlanGoalIndividuals.find((s) => s.number === num);
      return convertPrivateToRecord(res, num, SprintType.INDIVIDUAL);
    });
    return {
      longTerm: supportPlanGoalLong,
      shortTerm: supportPlanGoalShort,
      individuals: [...supportPlanGoalIndividuals]
    };
  };

  const supportPlanGoal = supplementSupportPlanGoal(
    fixPlanData.support_plan_goal
  );

  const filteredName: string[] = [];
  if (fixPlanData.participant) {
    fixPlanData.participant
      .filter((result) => result.name)
      .map((participant) => filteredName.push(participant.name));
  }
  const mtgData = [
    {
      itemTitle: "会議議事録",
      content: fixPlanData.evaluation_minutes
    }
  ];
  const hopeData = [
    {
      itemTitle: "本人・家族の意向",
      content: fixPlanData.user_request_text
    },
    {
      itemTitle: "本人の現状",
      content: fixPlanData.current_status
    }
  ];
  const longTermGoalData = [
    {
      itemTitle: "目標",
      content: supportPlanGoal.longTerm
        ? supportPlanGoal.longTerm.sprint_goal
        : ""
    }
  ];
  const shortTermGoalDatas = (short: SprintData): SectionPlanDataItem[] => {
    const result: SectionPlanDataItem[] = [
      {
        itemTitle: "目標",
        content: short.sprint_goal
      },
      {
        itemTitle: "本人の取組内容",
        content: short.adopt_info
      }
    ];

    if (props.displayOptions.display_info) {
      result.push({
        itemTitle: "職員の支援内容",
        content: short.support_info
      });
    }

    return result;
  };
  const otherData = [
    {
      itemTitle: "備考",
      content: fixPlanData.remarks
    },
    {
      itemTitle: "職員コメント",
      content: fixPlanData.staff_comment
    }
  ];
  const summaryData = [
    {
      itemTitle: "総括",
      content: fixPlanData.after_summary || ""
    },
    {
      itemTitle: "再評価日",
      content: fixPlanData.revaluation_date
        ? dateToLocalisedString(fixPlanData.revaluation_date, "YYYY年M月D日")
        : ""
    }
  ];
  const longTermGoalSubHeading = [
    {
      title: "設定日",
      date: supportPlanGoal.longTerm
        ? supportPlanGoal.longTerm.sprint_start_date
        : ""
    },
    {
      title: "達成予定日",
      date: supportPlanGoal.longTerm
        ? supportPlanGoal.longTerm.sprint_end_date
        : ""
    }
  ];
  const scheduleData = fixPlanData.support_plan_program.map((item) => {
    if (!trimString(item.scheduled_time) && !trimString(item.service_content)) {
      return {
        itemTitle: "",
        content: "",
        isContentEmpty: true
      };
    }
    return {
      itemTitle: trimString(item.scheduled_time) || "",
      content: trimString(item.service_content) || ""
    };
  });
  const dateForSubHeading = (data: Array<DateData>): JSX.Element => {
    const isAllDateEmpty = !data.some((dataItem: DateData) =>
      trimString(dataItem.date)
    );
    if (isAllDateEmpty) return <></>;
    return (
      <div>
        {data.map((item: DateData, index: number) => {
          const keyIndex = index;
          return (
            <span className={classes.dateContainer} key={keyIndex}>
              <span>{`${item.title}:`}</span>
              <span>
                {!item.date
                  ? "-"
                  : dateToLocalisedString(item.date, "YYYY年M月D日")}
              </span>
            </span>
          );
        })}
      </div>
    );
  };
  return (
    <div className={classes.page}>
      <header>
        <h1 className={classes.title}>個別支援計画書 モニタリング記録</h1>
      </header>
      <div className={classes.createdDate}>
        <span className={classes.counts}>
          計画作成日:
          {dateToLocalisedString(
            fixPlanData.creation_date !== null ? fixPlanData.creation_date : "",
            " YYYY年M月D日"
          )}
        </span>
      </div>
      <div className={classes.entryField}>
        <div className={classes.entryFieldColumn}>
          <div className={classes.entryFieldContainer}>
            <div className={classes.entryFieldLabel}>利用者名</div>
            <div className={classes.entryFieldContentContainer}>
              <span>{`${userDetail.nameSei}${userDetail.nameMei}`}</span>
              <span>様</span>
            </div>
          </div>
          <div className={classes.entryFieldContainer}>
            <div className={classes.entryFieldLabel}>事業所</div>
            <div className={classes.entryFieldContentContainer}>
              <span>{userDetail.facilityName}</span>
            </div>
          </div>
        </div>
        <div className={classes.entryFieldColumn}>
          <div className={classes.entryFieldContainer}>
            <div className={classes.entryFieldLabel}>受給者証番号</div>
            <div className={classes.entryFieldContentContainer}>
              <span>{userDetail.recipientNumber}</span>
            </div>
          </div>
          <div className={classes.entryFieldContainer}>
            <div className={classes.entryFieldLabel}>作成者</div>
            <div className={classes.entryFieldContentContainer}>
              <span>{authorValue}</span>
              {displayOptions.display_icon && (
                <span className={classes.entryFieldMark}>印</span>
              )}
            </div>
          </div>
        </div>
        <div className={classes.entryFieldColumn}>
          <div className={classes.entryFieldContainer}>
            <div className={classes.entryFieldLabel}>支援期間</div>
            <div className={classes.entryFieldContentContainer}>
              <span className="planningPeriodDate">
                {dateToLocalisedString(
                  fixPlanData.support_begin_date !== null
                    ? fixPlanData.support_begin_date
                    : "",
                  "YYYY年M月D日"
                )}
                ~
                {dateToLocalisedString(
                  fixPlanData.support_end_date !== null
                    ? fixPlanData.support_end_date
                    : "",
                  "YYYY年M月D日"
                )}
              </span>
            </div>
          </div>
          <div className={classes.entryFieldContainer}>
            <div className={classes.entryFieldLabel}>評価者</div>
            <div className={classes.entryFieldContentContainer}>
              <div>
                <span>{`${evaluationAuthorizerRole} ${evaluationAuthorizerName}`}</span>
              </div>
              {displayOptions.display_icon && (
                <span className={classes.entryFieldMark}>印</span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={classes.tableContainer}>
        <SectionTable
          classes={classes}
          sectionTitle="本人の状況"
          sectionPlanData={hopeData}
          secondaryStyle
        />
      </div>
      <div className={classes.tableContainer}>
        <SectionTable
          classes={classes}
          sectionTitle="長期目標"
          sectionPlanData={longTermGoalData}
          subHeading={dateForSubHeading(longTermGoalSubHeading)}
          shortOrLongTermGoalData={supportPlanGoal.longTerm}
          displayOptions={displayOptions}
        />
      </div>
      {supportPlanGoal.shortTerm.map((short) => (
        <div className={classes.tableContainer} key={`${short.id}`}>
          <SectionTable
            classes={classes}
            sectionTitle={`短期目標${short.number}`}
            sectionPlanData={shortTermGoalDatas(short)}
            shortOrLongTermGoalData={short}
            displayOptions={displayOptions}
          />
        </div>
      ))}
      <div className={classes.tableContainer}>
        <SectionTable
          classes={classes}
          sectionTitle="目標と支援の提供方針・内容"
          sectionPlanData={[
            {
              itemTitle: "",
              content: ""
            }
          ]}
          sprintPlanData={supportPlanGoal.individuals}
          displayOptions={displayOptions}
        />
      </div>
      <div className={classes.tableContainer}>
        <SectionTable
          classes={classes}
          sectionTitle="プログラム（1日の流れ）"
          sectionPlanData={scheduleData}
          displayOptions={displayOptions}
          schedule
        />
      </div>
      <div className={classes.tableContainer}>
        <SectionTable
          classes={classes}
          sectionTitle="その他"
          sectionPlanData={otherData}
          displayOptions={displayOptions}
        />
      </div>
      <div className={classes.tableContainer}>
        <SectionTable
          classes={classes}
          sectionTitle="実施後の変化（総括）"
          sectionPlanData={summaryData}
          displayOptions={displayOptions}
          possiblyHidden
        />
      </div>
      {displayOptions.display_minutes && (
        <div className={classes.tableContainer}>
          <SectionTable
            classes={classes}
            sectionTitle="モニタリング会議 議事録"
            implementDate={fixPlanData.evaluation_date || "-"}
            sectionPlanData={mtgData}
          />
        </div>
      )}
      <div className={classes.agreeTextContainer}>
        <p className={classes.agreeText}>
          上記計画書の内容についてサービス管理責任者より説明を受け、同意の上受領しました。
        </p>
        <div className={classes.signature}>
          <div className={classes.signatureDate}>
            <span>年</span>
            <span>月</span>
            <span>日</span>
          </div>
          <div
            className={`${classes.entryFieldContainer} ${classes.userNameContainer}`}
          >
            <div className={classes.userEntryFieldLabel}>利用者名</div>
            <div className={classes.userEntryFieldContentContainer}>
              {displayOptions.display_icon && (
                <span className={classes.entryFieldMark}>印</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const mapStateToProps = (state: AppState): StateProps => {
  const user = state.user as UserState;
  const userInFacility = state.GroupHome.userInFacility.user;
  const { facility } = state.GroupHome;
  return {
    user,
    userInFacility,
    facility,
    privateSupportPlan: state.GroupHome.supportPlan.privateSupportPlan
  };
};

export const EvaluationGroupHomePrint = connect(mapStateToProps)(
  withStyles(styles)(EvaluationGroupHomePrintCore)
);
